import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { StateEnum } from '@halo/chat-sdk';
import type { AvatarProps } from '@halo/ui-chat-react';
import { Avatar, RippleEffect, Tooltip } from '@halo/ui-chat-react';
import type { PropsWithChildren } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface UserItemProps extends Omit<BoxProps, 'role'> {
  name: React.ReactNode;
  secondary_name?: string;
  username: string;
  avatar: string;
  avatarProps?: AvatarProps;
  error?: boolean;
  onAvatarClick?: () => void;
  onClick?: () => void;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  userExtraElement?: React.ReactNode;
  userExtraElementRenderer?: (isHovered: boolean) => React.ReactNode;
  state: StateEnum;
  rippleEffect?: boolean;
  hoverable?: boolean;
  hoverableRounded?: string;
  onHoverChange?: (isHovered: boolean) => void;
  onlyFinalName?: boolean;
}

export function UserItem({
  username,
  avatar,
  avatarProps,
  error,
  onAvatarClick,
  onClick,
  startElement,
  endElement,
  userExtraElement,
  userExtraElementRenderer,
  state,
  rippleEffect = true,
  hoverable = true,
  hoverableRounded = 'initial',
  onHoverChange,
  onlyFinalName = true,
  ...props
}: UserItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isDelete = useMemo(() => state === StateEnum.DELETE_ACCOUNT, [state]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    onHoverChange?.(true);
  }, [onHoverChange]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHoverChange?.(false);
  }, [onHoverChange]);

  const { t } = useTranslation();

  const name = useMemo(() => {
    // custom node
    if (props.name && typeof props.name !== 'string') return props.name;

    if (state === StateEnum.DELETE_ACCOUNT) {
      return t('chat.deleteAccountText');
    }
    if (onlyFinalName) {
      return props.name;
    }
    return `${props.name ?? ''}${props.secondary_name && `(${props.secondary_name})`}`;
  }, [t, state, props.name, props.secondary_name, onlyFinalName]);

  return (
    <RippleEffect w="full" enabled={rippleEffect}>
      <RenderUserItem error={error}>
        <Box
          className="UserItem"
          px="2"
          pt="2"
          w="full"
          overflow="hidden"
          _hover={{
            ...(hoverable
              ? {
                  bg: 'bg.subtle',
                  rounded: hoverableRounded,
                }
              : {}),
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Flex gap="2.5" align="center">
            {startElement}
            <Avatar
              size="sm"
              showUserError={error}
              src={avatar}
              activeShake={false}
              onClick={onAvatarClick}
              {...avatarProps}
            />
            <Box
              flex="1"
              pb={isDelete ? '2' : '2'}
              borderBottom={isDelete ? '1px solid' : '1px solid'}
              borderColor="border.subtle"
              overflow="hidden"
              onClick={onClick}
              {...props}
            >
              <Flex align="center" justify="space-between" gap="2.5">
                <Box overflow="hidden">
                  <Box
                    color="fg"
                    fontSize="sm"
                    fontWeight="semibold"
                    lineHeight="normal"
                    truncate
                  >
                    {name}
                  </Box>
                  <Flex>
                    {username && (
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        lineHeight="normal"
                        color="fg.muted"
                        truncate
                      >
                        {isDelete && <>&nbsp;</>}
                        {!isDelete && <>@{username}</>}
                      </Text>
                    )}
                  </Flex>
                </Box>
                {userExtraElementRenderer
                  ? userExtraElementRenderer(isHovered)
                  : userExtraElement}
              </Flex>
            </Box>
            {endElement}
          </Flex>
        </Box>
      </RenderUserItem>
    </RippleEffect>
  );
}

const RenderUserItem = (props: PropsWithChildren<{ error?: boolean }>) => {
  const { error, children } = props;
  const { t } = useTranslation();
  if (error) {
    return (
      <Tooltip
        showArrow
        positioning={{ placement: 'top' }}
        openDelay={300}
        closeDelay={0}
        content={t('chat.deleteAccountText')}
      >
        {children}
      </Tooltip>
    );
  }
  return children;
};
